import { Modal } from '@components/core/modal.component';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@components/ui/accordion';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select';
import { Slider } from '@components/ui/slider';
import { Switch } from '@components/ui/switch';
import { SANKEY_FONT_SIZE_MAP } from '@utils/sankey.util';
import React, { ReactNode, useRef, useState } from 'react';
import { FormLabel } from './form-label.component';
import { ModalSidebar } from './modal-sidebar.component';
import {
  DEFAULT_HEIGHT,
  DEFAULT_LINK_COLOR,
  DEFAULT_LINK_OPACITY,
  DEFAULT_NODE_PADDING,
  DEFAULT_NODE_WIDTH,
  DEFAULT_WIDTH,
  INNER_COLUMN_AVAILABLE_RATIO,
  LABEL_MARGIN_RATIO,
  type LinkColorMode,
  MAX_LABEL_MARGIN,
  MIN_LABEL_WIDTH_ABS,
  MIN_LABEL_WIDTH_RATIO,
  type SankeySettings,
} from './sankey.component';

// =============================================================================
// TYPES
// =============================================================================

type SliderRange = {
  max: number;
  min: number;
  step: number;
};

/**
 * Default values and ranges for customization options
 */
export type SankeyCustomizationDefaults = {
  autoHeightAggressiveness: [number];
  autoHeightAggressivenessRange: SliderRange;
  height: [number];
  heightRange: SliderRange;
  hideSubtext: boolean;
  hoverLinkOpacity: [number];
  hoverLinkOpacityRange: SliderRange;
  innerColumnAvailableRatio: [number];
  innerColumnAvailableRatioRange: SliderRange;
  labelFontSize: [number];
  labelFontSizeRange: SliderRange;
  labelMarginRatio: [number];
  labelMarginRatioRange: SliderRange;
  labelMaxWidth: [number];
  labelMaxWidthRange: SliderRange;
  labelSubtextGap: [number];
  labelSubtextGapRange: SliderRange;
  linkColor: LinkColorMode;
  linkOpacity: [number];
  linkOpacityRange: SliderRange;
  dimmedLinkOpacity: [number];
  dimmedLinkOpacityRange: SliderRange;
  maxLabelMargin: [number];
  maxLabelMarginRange: SliderRange;
  minNodeHeight: [number];
  minNodeHeightRange: SliderRange;
  minLabelWidthAbs: [number];
  minLabelWidthAbsRange: SliderRange;
  minLabelWidthRatio: [number];
  minLabelWidthRatioRange: SliderRange;
  nodePadding: [number];
  nodePaddingRange: SliderRange;
  nodeWidth: [number];
  nodeWidthRange: SliderRange;
  sizeMode: 'auto' | 'manual';
  subtextFontSize: [number];
  subtextFontSizeRange: SliderRange;
  subtextMaxWidth: [number];
  subtextMaxWidthRange: SliderRange;
  verticalMargin: [number];
  verticalMarginRange: SliderRange;
  width: [number];
  widthRange: SliderRange;
};

// -----------------------------------------------------------------------------
// NEW TYPES FOR DATA FILTERS
// -----------------------------------------------------------------------------

/**
 * Option type for technology selection in the Sankey modal
 */
export type SelectOption = {
  id: string;
  name: string;
};

/**
 * Props for the SankeyModal component
 */
export type SankeyModalProps = {
  children: (props: SankeyModalRenderProps) => ReactNode;
  defaults?: Partial<SankeyCustomizationDefaults>;
  isOpen: boolean;
  onClose: () => void;
  productOptions?: SelectOption[];
  title: string;
  technologyOptions?: SelectOption[];
};

/**
 * Render props function signature
 */
export type SankeyModalRenderProps = {
  settings: Partial<SankeySettings>;
  selectedProductId?: string;
  selectedTechnologyId?: string;
};

// =============================================================================
// DEFAULTS
// =============================================================================

const DEFAULT_CUSTOMIZATION_DEFAULTS: SankeyCustomizationDefaults = {
  height: [DEFAULT_HEIGHT],
  heightRange: { min: 400, max: 1400, step: 50 },
  hideSubtext: false,
  hoverLinkOpacity: [0.3],
  hoverLinkOpacityRange: { min: 0, max: 1, step: 0.05 },
  innerColumnAvailableRatio: [INNER_COLUMN_AVAILABLE_RATIO],
  innerColumnAvailableRatioRange: { min: 0.3, max: 1.0, step: 0.05 },
  labelFontSize: [SANKEY_FONT_SIZE_MAP.xs.labelFontSize],
  labelFontSizeRange: { min: 8, max: 24, step: 1 },
  labelMarginRatio: [LABEL_MARGIN_RATIO],
  labelMarginRatioRange: { min: 0.05, max: 0.5, step: 0.05 },
  labelMaxWidth: [0],
  labelMaxWidthRange: { min: 0, max: 300, step: 10 },
  labelSubtextGap: [SANKEY_FONT_SIZE_MAP.xs.labelSubtextGap],
  labelSubtextGapRange: { min: 0, max: 50, step: 1 },
  linkColor: DEFAULT_LINK_COLOR,
  linkOpacity: [DEFAULT_LINK_OPACITY],
  linkOpacityRange: { min: 0.1, max: 1, step: 0.1 },
  dimmedLinkOpacity: [0.15],
  dimmedLinkOpacityRange: { min: 0, max: 0.6, step: 0.05 },
  maxLabelMargin: [MAX_LABEL_MARGIN],
  maxLabelMarginRange: { min: 20, max: 120, step: 4 },
  autoHeightAggressiveness: [2],
  autoHeightAggressivenessRange: { min: 1, max: 4, step: 0.1 },
  minNodeHeight: [12],
  minNodeHeightRange: { min: 4, max: 48, step: 1 },
  minLabelWidthAbs: [MIN_LABEL_WIDTH_ABS],
  minLabelWidthAbsRange: { min: 10, max: 50, step: 2 },
  minLabelWidthRatio: [MIN_LABEL_WIDTH_RATIO],
  minLabelWidthRatioRange: { min: 0.05, max: 0.4, step: 0.05 },
  nodePadding: [DEFAULT_NODE_PADDING],
  nodePaddingRange: { min: 2, max: 50, step: 1 },
  nodeWidth: [DEFAULT_NODE_WIDTH],
  nodeWidthRange: { min: 8, max: 30, step: 2 },
  sizeMode: 'auto',
  subtextFontSize: [SANKEY_FONT_SIZE_MAP.xs.subtextFontSize],
  subtextFontSizeRange: { min: 6, max: 20, step: 1 },
  subtextMaxWidth: [0],
  subtextMaxWidthRange: { min: 0, max: 300, step: 10 },
  verticalMargin: [24],
  verticalMarginRange: { min: 0, max: 80, step: 2 },
  width: [DEFAULT_WIDTH],
  widthRange: { min: 600, max: 1400, step: 50 },
};

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Reusable modal for Sankey visualizations with customization sidebar
 *
 * Uses the render props pattern to provide flexibility in the content
 * while offering consistent customization controls in the sidebar.
 */
export const SankeyModal: React.FC<SankeyModalProps> = (props) => {
  const { children, defaults, isOpen, onClose, title, technologyOptions, productOptions } = props;

  const mergedDefaults = { ...DEFAULT_CUSTOMIZATION_DEFAULTS, ...defaults };

  // Track whether height has been manually adjusted
  const [isHeightManuallySet, setIsHeightManuallySet] = useState(false);

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  // Track whether max width values have been manually adjusted
  const [isLabelMaxWidthManuallySet, setIsLabelMaxWidthManuallySet] = useState(false);
  const [isSubtextMaxWidthManuallySet, setIsSubtextMaxWidthManuallySet] = useState(false);

  // ---------------------------------------------------------------------------
  // STATE: technology filter
  // ---------------------------------------------------------------------------

  const [selectedTechnologyId, setSelectedTechnologyId] = useState<string>();
  const [selectedProductId, setSelectedProductId] = useState<string>();

  const [settings, setSettings] = useState({
    autoHeightAggressiveness: mergedDefaults.autoHeightAggressiveness,
    height: mergedDefaults.height,
    hideSubtext: mergedDefaults.hideSubtext,
    hoverLinkOpacity: mergedDefaults.hoverLinkOpacity,
    innerColumnAvailableRatio: mergedDefaults.innerColumnAvailableRatio,
    labelFontSize: mergedDefaults.labelFontSize,
    labelMarginRatio: mergedDefaults.labelMarginRatio,
    labelMaxWidth: mergedDefaults.labelMaxWidth,
    labelSubtextGap: mergedDefaults.labelSubtextGap,
    linkColor: mergedDefaults.linkColor,
    linkOpacity: mergedDefaults.linkOpacity,
    dimmedLinkOpacity: mergedDefaults.dimmedLinkOpacity,
    maxLabelMargin: mergedDefaults.maxLabelMargin,
    minNodeHeight: mergedDefaults.minNodeHeight,
    minLabelWidthAbs: mergedDefaults.minLabelWidthAbs,
    minLabelWidthRatio: mergedDefaults.minLabelWidthRatio,
    nodePadding: mergedDefaults.nodePadding,
    nodeWidth: mergedDefaults.nodeWidth,
    sizeMode: mergedDefaults.sizeMode,
    subtextFontSize: mergedDefaults.subtextFontSize,
    subtextMaxWidth: mergedDefaults.subtextMaxWidth,
    verticalMargin: mergedDefaults.verticalMargin,
    width: mergedDefaults.width,
  });

  // Throttle slider updates to reduce re-renders under heavy data
  const throttleMapRef = useRef<Record<string, number>>({});
  const THROTTLE_MS = 50;

  const handleChange = (key: keyof Omit<typeof settings, 'linkColor'>) => (value: number[]) => {
    if (key === 'height') setIsHeightManuallySet(true);
    if (key === 'labelMaxWidth') setIsLabelMaxWidthManuallySet(true);
    if (key === 'subtextMaxWidth') setIsSubtextMaxWidthManuallySet(true);

    const id = throttleMapRef.current[key as string];
    if (id) window.clearTimeout(id);
    throttleMapRef.current[key as string] = window.setTimeout(() => {
      setSettings((prev) => ({ ...prev, [key]: value as [number] }));
    }, THROTTLE_MS);
  };

  const sankeySettings: Partial<SankeySettings> = {
    // Use "auto" if height hasn't been manually set, otherwise use the slider value
    height: isHeightManuallySet ? settings.height[0] : 'auto',
    autoHeightAggressiveness: settings.autoHeightAggressiveness[0],
    hideSubtext: settings.hideSubtext,
    hoverLinkOpacity: settings.hoverLinkOpacity[0],
    innerColumnAvailableRatio: settings.innerColumnAvailableRatio[0],
    // Only pass font sizes if in manual mode, otherwise let auto-sizing work
    labelFontSize: settings.sizeMode === 'manual' ? settings.labelFontSize[0] : undefined,
    labelMarginRatio: settings.labelMarginRatio[0],
    // Use 0 (auto-calculate) if not manually set, otherwise use the slider value
    labelMaxWidth: isLabelMaxWidthManuallySet ? settings.labelMaxWidth[0] : 0,
    labelSubtextGap: settings.sizeMode === 'manual' ? settings.labelSubtextGap[0] : undefined,
    linkColor: settings.linkColor,
    linkOpacity: settings.linkOpacity[0],
    dimmedLinkOpacity: settings.dimmedLinkOpacity[0],
    maxLabelMargin: settings.maxLabelMargin[0],
    minNodeHeight: settings.minNodeHeight[0],
    minLabelWidthAbs: settings.minLabelWidthAbs[0],
    minLabelWidthRatio: settings.minLabelWidthRatio[0],
    nodePadding: settings.nodePadding[0],
    nodeWidth: settings.nodeWidth[0],
    subtextFontSize: settings.sizeMode === 'manual' ? settings.subtextFontSize[0] : undefined,
    // Use 0 (auto-calculate) if not manually set, otherwise use the slider value
    subtextMaxWidth: isSubtextMaxWidthManuallySet ? settings.subtextMaxWidth[0] : 0,
    verticalMargin: settings.verticalMargin[0],
    width: settings.width[0],
  };

  const {
    autoHeightAggressivenessRange,
    heightRange,
    innerColumnAvailableRatioRange,
    labelFontSizeRange,
    labelMarginRatioRange,
    labelMaxWidthRange,
    labelSubtextGapRange,
    linkOpacityRange,
    hoverLinkOpacityRange,
    dimmedLinkOpacityRange,
    maxLabelMarginRange,
    minNodeHeightRange,
    minLabelWidthAbsRange,
    minLabelWidthRatioRange,
    nodePaddingRange,
    nodeWidthRange,
    subtextFontSizeRange,
    subtextMaxWidthRange,
    verticalMarginRange,
    widthRange,
  } = mergedDefaults;

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      centered
      contentClassname="p-0"
      header={{
        title,
      }}
      isOpen={isOpen}
      onClose={onClose}
      width="95vw"
    >
      <ModalSidebar
        isOpen={isSidebarOpen}
        setIsOpen={setIsSidebarOpen}
        sidebarContent={
          <>
            {/* Technology Selector */}
            {technologyOptions && technologyOptions.length > 1 && (
              <div className="space-y-2 mb-4">
                <FormLabel label="Technology" />
                <Select
                  value={selectedTechnologyId ?? 'all'}
                  onValueChange={(value) => setSelectedTechnologyId(value === 'all' ? undefined : value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="All Technologies" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Technologies</SelectItem>
                    {technologyOptions.map((tech) => (
                      <SelectItem
                        key={tech.id}
                        value={tech.id}
                      >
                        {tech.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Product Selector */}
            {productOptions && productOptions.length > 1 && (
              <div className="space-y-2 mb-4">
                <FormLabel label="Product" />
                <Select
                  value={selectedProductId ?? 'all'}
                  onValueChange={(value) => setSelectedProductId(value === 'all' ? undefined : value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="All Products" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Products</SelectItem>
                    {productOptions.map((product) => (
                      <SelectItem
                        key={product.id}
                        value={product.id}
                      >
                        {product.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <Accordion
              type="multiple"
              className="w-full"
            >
              {/* Chart Dimensions */}
              <AccordionItem value="chart-dimensions">
                <AccordionTrigger className="text-sm font-semibold text-gray-800">Chart Dimensions</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4 pt-2">
                    {/* Auto-height Aggressiveness */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <FormLabel label="Auto-height Multiplier" />
                        <span className="text-sm text-gray-500">
                          {settings.autoHeightAggressiveness[0].toFixed(1)}×
                        </span>
                      </div>
                      <Slider
                        className="w-full"
                        max={autoHeightAggressivenessRange.max}
                        min={autoHeightAggressivenessRange.min}
                        onValueChange={handleChange('autoHeightAggressiveness')}
                        step={autoHeightAggressivenessRange.step}
                        value={settings.autoHeightAggressiveness}
                      />
                    </div>
                    {/* Height Control */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <FormLabel label="Height" />
                        <span className="text-sm text-gray-500">
                          {isHeightManuallySet ? `${settings.height[0]}px` : 'Auto'}
                        </span>
                      </div>
                      <Slider
                        className="w-full"
                        max={heightRange.max}
                        min={heightRange.min}
                        onValueChange={handleChange('height')}
                        step={heightRange.step}
                        value={settings.height}
                      />
                    </div>

                    {/* Width Control */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <FormLabel label="Width" />
                        <span className="text-sm text-gray-500">{settings.width[0]}px</span>
                      </div>
                      <Slider
                        className="w-full"
                        max={widthRange.max}
                        min={widthRange.min}
                        onValueChange={handleChange('width')}
                        step={widthRange.step}
                        value={settings.width}
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Label Settings */}
              <AccordionItem value="label-settings">
                <AccordionTrigger className="text-sm font-semibold text-gray-800">Label Settings</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4 pt-2">
                    {/* Size Mode Control */}
                    <div className="space-y-2">
                      <FormLabel label="Size Mode" />
                      <Select
                        defaultValue={settings.sizeMode}
                        onValueChange={(value) =>
                          setSettings((prev) => ({ ...prev, sizeMode: value as 'auto' | 'manual' }))
                        }
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="auto">Auto (Based on Available Space)</SelectItem>
                          <SelectItem value="manual">Manual (Custom Sizes)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {settings.sizeMode === 'manual' && (
                      <>
                        {/* Label Size Control */}
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <FormLabel label="Label Size" />
                            <span className="text-sm text-gray-500">{settings.labelFontSize[0]}px</span>
                          </div>
                          <Slider
                            className="w-full"
                            max={labelFontSizeRange.max}
                            min={labelFontSizeRange.min}
                            onValueChange={handleChange('labelFontSize')}
                            step={labelFontSizeRange.step}
                            value={settings.labelFontSize}
                          />
                        </div>

                        {/* Subtext Size Control */}
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <FormLabel label="Subtext Size" />
                            <span className="text-sm text-gray-500">{settings.subtextFontSize[0]}px</span>
                          </div>
                          <Slider
                            className="w-full"
                            max={subtextFontSizeRange.max}
                            min={subtextFontSizeRange.min}
                            onValueChange={handleChange('subtextFontSize')}
                            step={subtextFontSizeRange.step}
                            value={settings.subtextFontSize}
                          />
                        </div>

                        {/* Label-Subtext Gap Control */}
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <FormLabel label="Label-Subtext Gap" />
                            <span className="text-sm text-gray-500">{settings.labelSubtextGap[0]}px</span>
                          </div>
                          <Slider
                            className="w-full"
                            max={labelSubtextGapRange.max}
                            min={labelSubtextGapRange.min}
                            onValueChange={handleChange('labelSubtextGap')}
                            step={labelSubtextGapRange.step}
                            value={settings.labelSubtextGap}
                          />
                        </div>
                      </>
                    )}

                    {/* Label Max Width Control */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <FormLabel label="Label Max Width" />
                        <span className="text-sm text-gray-500">
                          {isLabelMaxWidthManuallySet ? `${settings.labelMaxWidth[0]}px` : 'Auto'}
                        </span>
                      </div>
                      <Slider
                        className="w-full"
                        max={labelMaxWidthRange.max}
                        min={labelMaxWidthRange.min}
                        onValueChange={handleChange('labelMaxWidth')}
                        step={labelMaxWidthRange.step}
                        value={settings.labelMaxWidth}
                      />
                    </div>

                    {/* Hide Subtext */}
                    <div className="flex items-center justify-between py-2">
                      <FormLabel label="Hide Subtext" />
                      <Switch
                        checked={settings.hideSubtext}
                        onCheckedChange={(checked) =>
                          setSettings((prev) => ({ ...prev, hideSubtext: Boolean(checked) }))
                        }
                      />
                    </div>

                    {/* Subtext Max Width Control */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <FormLabel label="Subtext Max Width" />
                        <span className="text-sm text-gray-500">
                          {isSubtextMaxWidthManuallySet ? `${settings.subtextMaxWidth[0]}px` : 'Auto'}
                        </span>
                      </div>
                      <Slider
                        className="w-full"
                        max={subtextMaxWidthRange.max}
                        min={subtextMaxWidthRange.min}
                        onValueChange={handleChange('subtextMaxWidth')}
                        step={subtextMaxWidthRange.step}
                        value={settings.subtextMaxWidth}
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Layout Settings */}
              <AccordionItem value="layout-settings">
                <AccordionTrigger className="text-sm font-semibold text-gray-800">Layout Settings</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4 pt-2">
                    {/* Min Node Height */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <FormLabel label="Min Node Height" />
                        <span className="text-sm text-gray-500">{settings.minNodeHeight[0]}px</span>
                      </div>
                      <Slider
                        className="w-full"
                        max={minNodeHeightRange.max}
                        min={minNodeHeightRange.min}
                        onValueChange={handleChange('minNodeHeight')}
                        step={minNodeHeightRange.step}
                        value={settings.minNodeHeight}
                      />
                    </div>

                    {/* Vertical Margin */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <FormLabel label="Top/Bottom Margin" />
                        <span className="text-sm text-gray-500">{settings.verticalMargin[0]}px</span>
                      </div>
                      <Slider
                        className="w-full"
                        max={verticalMarginRange.max}
                        min={verticalMarginRange.min}
                        onValueChange={handleChange('verticalMargin')}
                        step={verticalMarginRange.step}
                        value={settings.verticalMargin}
                      />
                    </div>
                    {/* Node Padding Control */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <FormLabel label="Node Padding" />
                        <span className="text-sm text-gray-500">{settings.nodePadding[0]}px</span>
                      </div>
                      <Slider
                        className="w-full"
                        max={nodePaddingRange.max}
                        min={nodePaddingRange.min}
                        onValueChange={handleChange('nodePadding')}
                        step={nodePaddingRange.step}
                        value={settings.nodePadding}
                      />
                    </div>

                    {/* Node Width Control */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <FormLabel label="Node Width" />
                        <span className="text-sm text-gray-500">{settings.nodeWidth[0]}px</span>
                      </div>
                      <Slider
                        className="w-full"
                        max={nodeWidthRange.max}
                        min={nodeWidthRange.min}
                        onValueChange={handleChange('nodeWidth')}
                        step={nodeWidthRange.step}
                        value={settings.nodeWidth}
                      />
                    </div>

                    {/* Label Margin Ratio Control */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <FormLabel label="Label Margin Ratio" />
                        <span className="text-sm text-gray-500">
                          {(settings.labelMarginRatio[0] * 100).toFixed(0)}%
                        </span>
                      </div>
                      <Slider
                        className="w-full"
                        max={labelMarginRatioRange.max}
                        min={labelMarginRatioRange.min}
                        onValueChange={handleChange('labelMarginRatio')}
                        step={labelMarginRatioRange.step}
                        value={settings.labelMarginRatio}
                      />
                    </div>

                    {/* Max Label Margin Control */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <FormLabel label="Max Label Margin" />
                        <span className="text-sm text-gray-500">{settings.maxLabelMargin[0]}px</span>
                      </div>
                      <Slider
                        className="w-full"
                        max={maxLabelMarginRange.max}
                        min={maxLabelMarginRange.min}
                        onValueChange={handleChange('maxLabelMargin')}
                        step={maxLabelMarginRange.step}
                        value={settings.maxLabelMargin}
                      />
                    </div>

                    {/* Inner Column Available Ratio Control */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <FormLabel label="Inner Column Available Ratio" />
                        <span className="text-sm text-gray-500">
                          {(settings.innerColumnAvailableRatio[0] * 100).toFixed(0)}%
                        </span>
                      </div>
                      <Slider
                        className="w-full"
                        max={innerColumnAvailableRatioRange.max}
                        min={innerColumnAvailableRatioRange.min}
                        onValueChange={handleChange('innerColumnAvailableRatio')}
                        step={innerColumnAvailableRatioRange.step}
                        value={settings.innerColumnAvailableRatio}
                      />
                    </div>

                    {/* Min Label Width Ratio Control */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <FormLabel label="Min Label Width Ratio" />
                        <span className="text-sm text-gray-500">
                          {(settings.minLabelWidthRatio[0] * 100).toFixed(0)}%
                        </span>
                      </div>
                      <Slider
                        className="w-full"
                        max={minLabelWidthRatioRange.max}
                        min={minLabelWidthRatioRange.min}
                        onValueChange={handleChange('minLabelWidthRatio')}
                        step={minLabelWidthRatioRange.step}
                        value={settings.minLabelWidthRatio}
                      />
                    </div>

                    {/* Min Label Width Absolute Control */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <FormLabel label="Min Label Width (Absolute)" />
                        <span className="text-sm text-gray-500">{settings.minLabelWidthAbs[0]}px</span>
                      </div>
                      <Slider
                        className="w-full"
                        max={minLabelWidthAbsRange.max}
                        min={minLabelWidthAbsRange.min}
                        onValueChange={handleChange('minLabelWidthAbs')}
                        step={minLabelWidthAbsRange.step}
                        value={settings.minLabelWidthAbs}
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Link Settings */}
              <AccordionItem value="link-settings">
                <AccordionTrigger className="text-sm font-semibold text-gray-800">Link Settings</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4 pt-2">
                    {/* Link Color Control */}
                    <div className="space-y-2">
                      <FormLabel label="Link Color Mode" />
                      <Select
                        defaultValue={settings.linkColor}
                        onValueChange={(value) =>
                          setSettings((prev) => ({ ...prev, linkColor: value as LinkColorMode }))
                        }
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="source">Source Color</SelectItem>
                          <SelectItem value="target">Target Color</SelectItem>
                          <SelectItem value="source-target">Source to Target Gradient</SelectItem>
                          <SelectItem value="static">Static Gray</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Link Opacity Control */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <FormLabel label="Link Opacity" />
                        <span className="text-sm text-gray-500">{Math.round(settings.linkOpacity[0] * 100)}%</span>
                      </div>
                      <Slider
                        value={settings.linkOpacity}
                        onValueChange={handleChange('linkOpacity')}
                        min={linkOpacityRange.min}
                        max={linkOpacityRange.max}
                        step={linkOpacityRange.step}
                        className="w-full"
                      />
                    </div>

                    {/* Highlighted Link Opacity */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <FormLabel label="Highlighted Link Opacity" />
                        <span className="text-sm text-gray-500">{Math.round(settings.hoverLinkOpacity[0] * 100)}%</span>
                      </div>
                      <Slider
                        value={settings.hoverLinkOpacity}
                        onValueChange={handleChange('hoverLinkOpacity')}
                        min={hoverLinkOpacityRange.min}
                        max={hoverLinkOpacityRange.max}
                        step={hoverLinkOpacityRange.step}
                        className="w-full"
                      />
                    </div>

                    {/* Dimmed Link Opacity */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <FormLabel label="Dimmed Link Opacity" />
                        <span className="text-sm text-gray-500">
                          {Math.round(settings.dimmedLinkOpacity[0] * 100)}%
                        </span>
                      </div>
                      <Slider
                        value={settings.dimmedLinkOpacity}
                        onValueChange={handleChange('dimmedLinkOpacity')}
                        min={dimmedLinkOpacityRange.min}
                        max={dimmedLinkOpacityRange.max}
                        step={dimmedLinkOpacityRange.step}
                        className="w-full"
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </>
        }
      >
        <div className="flex flex-1 flex-col gap-4 overflow-y-auto rounded-xl bg-gray-50 p-4">
          <div className="flex-1 flex items-center justify-center w-full m-auto min-h-[400px]">
            {children({ settings: sankeySettings, selectedProductId, selectedTechnologyId })}
          </div>
        </div>
      </ModalSidebar>
    </Modal>
  );
};
